import {useParams, Link} from "react-router";
import {useEffect, useState} from 'react';
import {Button, Card, Divider, Row, Col, Input, Select, message} from 'antd';
import {
    updateMomPlant,
    getMomEvents,
    getPlant,
    destroyClone,
    transferPlant,
    resetMomPlant,
    harvestPlant, getPlantTags, removeTagPlant,
    getPlantRows, getPlantStrain, updatePlantStrain,
} from "../../api/Api";
import {toFSDateTime} from "../../util/timeutils";
import {MomEventTable} from "./MomEventTable";
import {DestroyModal} from "./DestroyModal";
import {TransferModal} from "./TransferModal";
import {ResetMomModal} from "./ResetMomModal";
import {StrainType, getOptions, MomEventType, PlantStageType} from "../../api/Constants";
import {HarvestModal} from "./HarvestModal";
import {PlantTagWidget} from "./PlantTagWidget";
import {TagSelectWidget} from "./TagSelectWidget";
import {handleNoSession} from "../../util/errorUtils";
import {getPlantRoomTransferOptions} from "./PlantTransferUtil";
import {StrainSelectWidget} from "./StrainSelectWidget";

const {TextArea} = Input;

export const CloneView = ({session, roomMap, refresh, onRefresh, onApiFail, props}) => {
    const params = useParams();
    const plantId = params.plantId;
    const [name, setName] = useState(undefined);
    const [strain, setStrain] = useState(undefined);
    const [description, setDescription] = useState(undefined);
    const [metricTag, setMetricTag] = useState(undefined);
    const [events, setEvents] = useState([]);
    const [plantStrain, setPlantStrain] = useState(undefined);

    const [destroyModalOpen, setDestroyModalOpen] = useState(false);
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [transferBackModalOpen, setTransferBackModalOpen] = useState(false);
    const [momModalOpen, setMomModalOpen] = useState(false);
    const [harvestModalOpen, setHarvestModalOpen] = useState(false);

    const [plant, setPlant] = useState(undefined);
    const [tags, setTags] = useState([]);

    const [rowMap, setRowMap] = useState({});

    const [messageApi, messageContext] = message.useMessage();
    const [pageRefresh, setPageRefresh] = useState(0);
    const io = {onApiFail, refresh, onRefresh}

    useEffect(() => {
        getPlant(plantId).then(r => {
            if ((r === null) || (r === undefined)) {
                return;
            }
            setPlant(r);
            setName(r.name);
            setStrain(r.strain);
            setDescription(r.description);
            setMetricTag(r.metricTag);

            if (r.strainId) {
                getPlantStrain(r.strainId).then(res => {
                    setPlantStrain(res);
                }).catch(e => {
                    handleNoSession(e, onApiFail);
                });
            }
            return getMomEvents(plantId);
        }).then(evs => {
            const mainEvents = evs.map(ev => {
                return {
                    ...ev,
                    key: ev.id
                }
            }).filter(ev => {
                // fixme this is a bit awkward due to the enum value definition
                return ((ev.event <= MomEventType.Destroy) || (ev.event >= MomEventType.Transfer))
            }).sort((a, b) => {
                return new Date(a.date) - new Date(b.date)
            });
            setEvents(mainEvents);
        }).catch (e => {
            handleNoSession(e, onApiFail);
        });
    }, [plantId, onApiFail, pageRefresh]);

    useEffect(() => {
        if (plant === undefined) {
            // no plant set yet
            return;
        }

        getPlantRows(plant.roomId).then(r => {
            const nextMap = {};
            r.forEach(row => {
                nextMap[row.id] = {...row};
            });
            setRowMap(nextMap);
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }, [plant, refresh, onApiFail]);

    useEffect(() => {
        getPlantTags(plantId).then(r => {
            setTags(r.map(tag => {
                return {...tag, key: tag.id}
            }))
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })
    }, [plantId, refresh, onApiFail])


    const onNameChange = (e) => {
        setName(e.target.value);
    }

    const onStrainChange = (val) => {
        setStrain(val);
    }
    const onDescriptionChange = (e) => {
        setDescription(e.target.value);
    }

    const onTagChange = (e) => {
        setMetricTag(e.target.value);
    }

    const onPlantStrainChange = (val) => {
        updatePlantStrain(plantId, val.id).then(r => {
            if (onRefresh) { onRefresh() }
            setPageRefresh(pageRefresh+1);
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const onSave = () => {
        const options = {};
        // set the fields that change
        if (name !== plant.name) {
            options.name = name;
        }
        if (strain !== plant.strain) {
            options.strain = strain;
        }
        if (description !== plant.description) {
            options.description = description;
        }
        if (metricTag !== plant.metricTag) {
            options.metricTag = metricTag;
        }

        // update the mom
        updateMomPlant(plantId, options).then(r => {
            if (onRefresh) { onRefresh() }
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onDestroyModalOpen = () => {
        setDestroyModalOpen(true);
    }

    const onDestroyOk = (numClones) => {
        destroyClone(plantId, numClones, `destroyed ${numClones}`).then(r => {
            if (onRefresh) { onRefresh() }
            setPageRefresh(pageRefresh+1);
        }).catch(e => {
            console.log(`destroy clones failed`);
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
        setDestroyModalOpen(false);
    }

    const onDestroyCancel = () => {
        setDestroyModalOpen(false);
    }

    const onTransferModalOpen = () => {
        setTransferModalOpen(true);
    }

    const onTransferBackModalOpen = () => {
        setTransferBackModalOpen(true);
    }

    const onTransferOk = (cleanup) => (numClones, selectedRoom) => {
        const room = roomMap[selectedRoom];
        const roomId = selectedRoom;
        let {type: roomType} = room;

        // the next stage will match the roomType of the destination room
        const nextStage = roomType;

        transferPlant(plantId, numClones, nextStage, roomId).then(r => {
            if (onRefresh) { onRefresh() }
        }).catch(e => {
            console.log(`transfer plants failed`);
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        if (cleanup) {
            cleanup();
        }
    }

    const onTransferCancel = () => {
        setTransferModalOpen(false);
    }

    const onTransferBackCancel = () => {
        setTransferBackModalOpen(false);
    }

    const onMomModalOpen = () => {
        setMomModalOpen(true);
    }

    const onMomModalCancel = () => {
        setMomModalOpen(false);
    }

    const onMomModalOk = (count, notes, createdBy) => {
        // createdBy is a user, extract the id
        const {id} = createdBy;
        resetMomPlant(plantId, count, notes, id).then(r => {
            if (onRefresh) { onRefresh() }
            messageApi.open({
                type: 'success',
                content: `Reset ${count} plants as Moms`,
            });
        }).catch(e => {
            console.log(`transfer plants failed`);
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
        setMomModalOpen(false);
    }

    const onHarvestModalOpen = () => {
        setHarvestModalOpen(true);
    }

    const onHarvestModalCancel = () => {
        setHarvestModalOpen(false);
    }

    const onHarvestModalOk = (notes, createdBy, date) => {
        // createdBy is a user, extract the id
        const {id} = createdBy;
        harvestPlant(plantId, notes, id, date).then(r => {
            if (onRefresh) { onRefresh() }
            messageApi.open({
                type: 'success',
                content: `Plants Harvested`,
            });
        }).catch(e => {
            messageApi.open({
                type: 'error',
                content: `Harvest Plants Failed`,
            });
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
        setHarvestModalOpen(false);
    }

    const onTagClick = (tag) => () => {
        removeTagPlant(tag.id, plantId).then(r => {
            if (onRefresh) onRefresh();
        }).catch (e => {
            handleNoSession(e, onApiFail);
        })
    }

    const divStyle = {
        textAlign: 'left'
    }

    const rowStyle = {
        marginTop: '5px',
        marginBottom: '5px'
    }

    if (plant === undefined) {
        return <div />
    }


    // save is enabled if the user changes anything
    const saveDisabled = (name === plant.name) && (description === plant.description) && (metricTag === plant.metricTag) && (strain === plant.strain);

    const strainOptions = getOptions(StrainType) ();

    const {startCount, currentCount} = plant;
    // do not allow plants to be destroyed more than once or cloned
    const destroyDisabled = currentCount <= 0;

    let roomOptions = [];
    let roomAlternateOptions = [];
    const {stage, roomId: plantRoomId} = plant;

    if (Object.keys(roomMap).length > 0) {
        // guard this section since roomMap might not be ready
        roomOptions = getPlantRoomTransferOptions("cultivation", roomMap, plantRoomId, stage, false);
        roomAlternateOptions = getPlantRoomTransferOptions("cultivation", roomMap, plantRoomId, stage, true);
    }

    const transferDisabled = stage === PlantStageType.Harvest;
    const transferBackDisabled = stage === PlantStageType.Clone;

    const transferOkCleanup = () => {
        setTransferModalOpen(false);
    }

    const transferBackCleanup = () => {
        setTransferBackModalOpen(false);
    }

    return <div style={divStyle}>
        {messageContext}
        <DestroyModal open={destroyModalOpen} onOk={onDestroyOk} onCancel={onDestroyCancel} max={currentCount} />
        <TransferModal roomOptions={roomOptions} open={transferModalOpen} defaultCount={currentCount} onOk={onTransferOk(transferOkCleanup)} onCancel={onTransferCancel} max={currentCount} />
        <TransferModal roomOptions={roomAlternateOptions} open={transferBackModalOpen} defaultCount={currentCount} onOk={onTransferOk(transferBackCleanup)} onCancel={onTransferBackCancel} max={currentCount} />
        <ResetMomModal user={session} open={momModalOpen} onOk={onMomModalOk} onCancel={onMomModalCancel} max={currentCount} />
        <HarvestModal user={session} open={harvestModalOpen} onOk={onHarvestModalOk} onCancel={onHarvestModalCancel} count={currentCount} />
        <Card title={<b>Clone</b>}>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Name:</b>
                </Col>
                <Col span={8}>
                    <Input onChange={onNameChange} value={name} default={'Name'} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Description:</b>
                </Col>
                <Col span={8}>
                    <TextArea onChange={onDescriptionChange} value={description} default={'Description'} rows={4} maxLength={256} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Strain:</b>
                </Col>
                <Col span={8}>
                    <Col><Select options={strainOptions} placeholder={"strain"} onChange={onStrainChange} value={strain} /></Col>
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Metrc Tag:</b>
                </Col>
                <Col span={8}>
                    <Input onChange={onTagChange} value={metricTag} default={'Metrc Tag'} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Created:</b>
                </Col>
                <Col span={8}>
                    {`${toFSDateTime(new Date(plant.creationTime))} by ${plant.username}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Room:</b>
                </Col>
                <Col span={8}>
                    {roomMap[plant.roomId].name}
                </Col>
            </Row>
            {(plant.rowId) && (rowMap[plant.rowId]) && <Row style={rowStyle}>
                <Col span={2}><b>Row:</b></Col>
                <Col span={8}>{rowMap[plant.rowId].rowName}</Col>
            </Row>
            }
            <Row style={{...rowStyle, marginBottom: '10px'}}>
                <Col span={2} style={{marginTop: '5px'}}>
                    <b>Strain:</b>
                </Col>
                <Col>
                    <StrainSelectWidget strain={plantStrain} io={io} onSelect={onPlantStrainChange} defaultActive={false}/>
                </Col>
            </Row>
            {(plantStrain !== undefined) &&
                <Row style={{...rowStyle, marginBottom: '10px'}}>
                    <Col span={2}>
                    </Col>
                    <Col>
                        <Link to={`/cultivationapp/strain/${plantStrain.id}`}>Link to strain {plantStrain.name}</Link>
                    </Col>
                </Row>
            }
            <Row style={rowStyle}>
                <Button onClick={onSave} disabled={saveDisabled}>Save Changes</Button>
            </Row>
            <Divider />
            <Row style={{...rowStyle, marginBottom: '10px'}}>
                <Col span={2}>
                    <b>Tags:</b>
                </Col>
                <Col>
                    <Row>
                        {(tags.map(t => {
                            return <PlantTagWidget key={t.id} tag={t} onClick={onTagClick(t)} />
                        }))}
                    </Row>
                </Col>
            </Row>
            <Row style={rowStyle}>
                <TagSelectWidget plantId={plantId} onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} />
            </Row>
        </Card>
        <Card title={<b>Counts</b>}>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Current Count:</b>
                </Col>
                <Col span={8}>
                    {`${currentCount}`}
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}>
                    <b>Starting Count:</b>
                </Col>
                <Col span={8}>
                    {`${startCount}`}
                </Col>
            </Row>
        </Card>
        <Card title={<b>Actions</b>}>
            <Row style={rowStyle}>
                <Col span={2}>
                    <Button onClick={onDestroyModalOpen} disabled={destroyDisabled}>Destroy</Button>
                </Col>
                <Col span={2}>
                    <Button onClick={onTransferModalOpen} disabled={transferDisabled}>Transfer</Button>
                </Col>
                <Col span={3}>
                    <Button onClick={onTransferBackModalOpen} disabled={transferBackDisabled}>Undo Transfer</Button>
                </Col>
                {(stage === PlantStageType.Veg) &&
                    <Col span={2}>
                        <Button onClick={onMomModalOpen} >Reset Mom</Button>
                    </Col>
                }
                {((stage === PlantStageType.Harvest) && (currentCount > 0)) &&
                    <Col span={2}>
                        <Button onClick={onHarvestModalOpen}>Harvest</Button>
                    </Col>
                }
            </Row>
        </Card>
        <Card title={<b>Events</b>}>
            <MomEventTable events={events} plant={plant} onApiFail={onApiFail} onRefresh={onRefresh} />
        </Card>
    </div>
}